import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Checkbox, Collapse, Grid, List, ListItem, ListItemSecondaryAction, ListItemText, ListSubheader, makeStyles, TextField } from '@material-ui/core'
import ExpandLess from '@material-ui/icons/ExpandLess'
import ExpandMore from '@material-ui/icons/ExpandMore'

import Utilities from '../../utils/Utilities'
import DocLink from '../../docs/DocLink'

const useStyles = makeStyles(theme => ({
  propertyText: {
    textAlign: 'right',
    marginRight: '1rem'
  },
  listHeader: {
    backgroundColor: 'white',
    display: 'flex',
    padding: '1rem',
    justifyContent: 'space-between',
    minWidth: '50ch'
  },
  permissionGridContainer: {
    marginBottom: '1rem'
  },
  docLink: {verticalAlign: 'sub', marginLeft: '0.5rem', display: 'inline-flex'}
}))

/**
 * return an object with human readable properties
 * @param {Object} obj object with properties to convert to human readable
 * @example
 * {
 *    account_securityGroups: {
 *      read: true,
 *      read_advanced: true
 *    }
 * } => {
 *    "Account Security Groups": {
 *      "Read": true,
 *      "Read Advanced": true
 *    }
 * }
 */
const getHumanReadableObject = obj => {
  let humanReadable
  // loop through each property of object to check if it is also an object
  // if the properties value is type object convert that object as well
  if (typeof obj === 'object' && obj !== null) {
    humanReadable = { ...obj }
    Object.keys(obj).forEach(key => {
      humanReadable[Utilities.getHumanReadable(key)] = getHumanReadableObject(obj[key])
      delete humanReadable[key]
    })
    return humanReadable
  } else {
    return obj
  }
}

/**
 * return an object with properties that have values of the replacement key that property would use
 * and also the reverse mapping
 * @param {Object} obj original object to get the key replacements for
 * @example
 * {
 *    helloWorld: {...},
 * } => {
 *    helloWorld: "Hello World",
 *    "Hello World": "helloWorld"
 * }
 */
const getHumanReadableConversion = obj => {
  const mapping = {}
  Object.keys(obj).forEach(key => {
    /**
      @TODO: handle or throw error if there are duplicate mappings
      eg: {"a-a":{...}, "a_a": {...}}
      which both map to the same value "A A"
    */
    const readable = Utilities.getHumanReadable(key)
    mapping[key] = readable
    mapping[readable] = key
  })
  return mapping
}

const PermissionProperty = props => {
  const { readOnly, text, checked, onChange } = props
  const classes = useStyles()
  return <ListItem>
    <ListItemText
      className={classes.propertyText}
      primary={text}
    />
    <ListItemSecondaryAction>
      <Checkbox
        disabled={readOnly}
        checked={checked}
        onChange={(ev) => onChange(ev.target.checked)}
      />
    </ListItemSecondaryAction>
  </ListItem>
}

const PermissionItem = props => {
  const { readOnly, permission, setIsOpen, selectPermission, selectProperty, name, isOpen } = props
  const handleSelectProperty = (selected, property) => {
    selectProperty(selected, property)
  }

  const handleTogglePermission = () => {
    setIsOpen(!isOpen)
  }

  const allSelected = Object.keys(permission).every(property => permission[property])

  return <>
    <ListItem button onClick={handleTogglePermission} >
      <ListItemText primary={Utilities.getHumanReadable(name)} />
      <Checkbox
        color="secondary"
        disabled={readOnly}
        checked={allSelected}
        indeterminate={!allSelected && Object.keys(permission).some(property => permission[property])}
        onChange={e => {
          selectPermission()
        }}
        onClick={e => e.stopPropagation()}
      />
      {isOpen ? <ExpandLess /> : <ExpandMore />}
    </ListItem>
    <Collapse in={isOpen} timeout="auto" unmountOnExit>
      <List dense disablePadding>
        {Object.keys(permission).map((property, i) => {
          return <PermissionProperty
            key={'permission property' + property + i}
            readOnly={readOnly}
            text={Utilities.getHumanReadable(property)}
            checked={permission[property]}
            onChange={(checked) => handleSelectProperty(checked, property)}
          />
        })}
      </List>
    </Collapse>
  </>
}

const SecurityRoleTreeView = props => {
  const { role, onChange } = props
  const [openPaths, setOpenPaths] = useState([])
  const [filter, setFilter] = useState('')

  const isOwnRole = useSelector(state => state.data.user.userRole) == role.roleID

  const classes = useStyles()

  const handleSetOpenPath = (open, path) => {
    // if the path should open add it to the open paths else filter it
    if (open) {
      setOpenPaths([...openPaths, path])
    } else {
      setOpenPaths(openPaths.filter(openPath => openPath !== path))
    }
  }

  const handleSelectProperty = (permission, selected, property) => {
    // toggle the permission property
    // data structure: role = {..., roleSchema: { permission: { properties: boolean, ...} }}
    const newRoleSchema = { ...role.roleSchema }
    newRoleSchema[permission] = { ...newRoleSchema[permission], [property]: selected }
    onChange({ ...role, roleSchema: newRoleSchema })
  }

  const handleSelectPermission = (permission) => {
    // check if the permission has any properties selected
    // if there are then deselect them all otherwise select them all
    const newRoleSchema = { ...role.roleSchema }
    const permissionProperties = Object.keys(newRoleSchema[permission])
    let select = true
    if (permissionProperties.some(property => newRoleSchema[permission][property])) {
      select = false
    }
    permissionProperties.forEach(property => {
      newRoleSchema[permission] = { ...newRoleSchema[permission], [property]: select }
    })
    onChange({ ...role, roleSchema: newRoleSchema })
  }

  const readOnly = !onChange || parseInt(role.roleID) <= 100 || isOwnRole

  return (
    <List component="nav">
      <ListSubheader component="div" className={classes.listHeader}>
        Role Permissions <div>
          <TextField size="small" placeholder="Search" variant="outlined" value={filter} onChange={ev => setFilter(ev.target.value)} />
          <DocLink className={classes.docLink} path="https://invintus-support.freshdesk.com/a/solutions/articles/48001161310" />
        </div>
      </ListSubheader>
      <Grid container>
        {Object.keys(role.roleSchema).sort().map((key, i) => {
          if (!!filter && !Utilities.getHumanReadable(key).toLowerCase().includes(filter.toLowerCase())) {
            return null
          }
          return <Grid key={'permission item' + i} item xs={12} md={6} className={classes.permissionGridContainer}>
            <PermissionItem
              readOnly={readOnly}
              selectPermission={() => {
                if (onChange) {
                  handleSelectPermission(key)
                }
              }}
              selectProperty={(selected, property) => {
                if (onChange) {
                  handleSelectProperty(key, selected, property)
                }
              }}
              setIsOpen={(open) => handleSetOpenPath(open, key)}
              permission={role.roleSchema[key]}
              name={key}
              isOpen={openPaths.includes(key)}
            />
          </Grid>
        })}
      </Grid>
    </List>
  )
}

export default SecurityRoleTreeView
